/**
 * Which data source this page load uses. Demo data can only ever run on
 * localhost and *.vercel.app previews: every other host gets live data,
 * whatever the query string, the session or the build env says. A production
 * build refuses demo too (production deployments also answer on a
 * *.vercel.app alias).
 *
 * Order:
 * 1. Any host other than localhost or *.vercel.app → live (Welthra V2).
 * 2. Production build (VERCEL_ENV=production at build time) → live.
 * 3. ?data=live or ?data=demo on the URL (remembered for the tab session).
 * 4. VITE_DATA_MODE if set (npm run dev:demo sets it).
 * 5. Otherwise demo.
 */
import type { DataMode } from "./types";

export const PRODUCTION_HOSTS = ["my.taxfreewealthplan.com"] as const;

const SESSION_KEY = "tfwp.dataMode";

export interface ModeInputs {
  hostname: string;
  search: string;
  /** import.meta.env.VITE_DATA_MODE */
  envMode: string | undefined;
  /** VERCEL_ENV captured at build time ("production" | "preview" | "development" | ""). */
  vercelEnv: string | undefined;
  /** Value remembered for this tab session, if any. */
  remembered: string | null;
}

function asMode(value: string | null | undefined): DataMode | null {
  if (value === "demo") return "demo";
  if (value === "live" || value === "supabase") return "live";
  return null;
}

/** Lower case, without the trailing dot a fully qualified name may carry ("my.taxfreewealthplan.com."). */
export function normalizeHost(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.+$/, "");
}

export function isProductionHost(hostname: string): boolean {
  const host = normalizeHost(hostname);
  return PRODUCTION_HOSTS.some((h) => host === h || host === `www.${h}`);
}

/** The only hosts that may ever show demo data. */
export function isPreviewOrLocalHost(hostname: string): boolean {
  const host = normalizeHost(hostname);
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]" || /^[a-z0-9-]+(\.[a-z0-9-]+)*\.vercel\.app$/.test(host);
}

export function resolveDataMode(input: ModeInputs): DataMode {
  if (!isPreviewOrLocalHost(input.hostname)) return "live";
  if (input.vercelEnv === "production") return "live";

  const fromUrl = asMode(new URLSearchParams(input.search).get("data"));
  if (fromUrl) return fromUrl;
  const remembered = asMode(input.remembered);
  if (remembered) return remembered;

  const fromEnv = asMode(input.envMode);
  if (fromEnv) return fromEnv;

  return "demo";
}

/** Reads the real browser inputs and remembers a ?data= choice for the tab session. */
export function currentDataMode(): DataMode {
  let remembered: string | null = null;
  try {
    remembered = sessionStorage.getItem(SESSION_KEY);
  } catch {
    /* storage blocked: fall through */
  }
  const mode = resolveDataMode({
    hostname: window.location.hostname,
    search: window.location.search,
    envMode: import.meta.env.VITE_DATA_MODE as string | undefined,
    vercelEnv: __VERCEL_ENV__,
    remembered,
  });
  const fromUrl = asMode(new URLSearchParams(window.location.search).get("data"));
  if (fromUrl && isPreviewOrLocalHost(window.location.hostname)) {
    try {
      sessionStorage.setItem(SESSION_KEY, fromUrl);
    } catch {
      /* ignore */
    }
  }
  return mode;
}
